import { useCallback, useEffect, useState } from 'react';
import { Globe2, RefreshCw, Server, ShieldCheck } from 'lucide-react';
import { api, jsonBody } from './api';

type Runtime = {
  version: string;
  base_domain: string;
  dns_provider: string;
  max_upload_bytes: number;
  allow_preprovisioned: boolean;
  registration_enabled: boolean;
};

type Notify = (type: 'success' | 'error', message: string) => void;

const bytes = (value: number) => value < 1024 ** 2 ? `${(value / 1024).toFixed(1)} KiB` : value < 1024 ** 3 ? `${(value / 1024 ** 2).toFixed(1)} MiB` : `${(value / 1024 ** 3).toFixed(2)} GiB`;
const messageOf = (error: unknown) => error instanceof Error ? error.message : '操作失败';

export function SystemSettings({ notify }: { notify: Notify }) {
  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [registration, setRegistration] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [info, reg] = await Promise.all([
        api<Runtime>('/api/admin/settings/runtime'),
        api<{ enabled: boolean }>('/api/admin/settings/registration'),
      ]);
      setRuntime(info);
      setRegistration(reg.enabled);
      setError('');
    } catch (cause) {
      setError(messageOf(cause));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function toggleRegistration() {
    if (registration === null || saving) return;
    setSaving(true);
    setError('');
    try {
      const result = await api<{ enabled: boolean }>('/api/admin/settings/registration', {
        method: 'PUT', body: jsonBody({ enabled: !registration }),
      });
      setRegistration(result.enabled);
      notify('success', result.enabled ? '邀请注册已开启' : '邀请注册已关闭');
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-stack settings-page">
      <section className="panel settings-card">
        <div className="settings-heading">
          <div><strong>邀请注册</strong><span>关闭后，<code>/api/auth/register</code> 会拒绝所有新邀请码注册；已登录账号不受影响。</span></div>
          <div className="inline-actions">
            <span className={`state-pill ${registration ? 'active' : 'disabled'}`}><i />{registration === null ? '读取中' : registration ? '已开启' : '已关闭'}</span>
            <button type="button" className="icon-button" aria-label="刷新系统设置" title="刷新系统设置" onClick={() => void load()}><RefreshCw size={17} /></button>
          </div>
        </div>
        <div className="settings-actions">
          <button type="button" className={registration ? 'danger-button' : 'primary-button'} disabled={registration === null || saving} onClick={() => void toggleRegistration()}>
            <ShieldCheck size={16} />{saving ? '正在保存…' : registration ? '关闭邀请注册' : '开启邀请注册'}
          </button>
        </div>
      </section>

      {error && <div className="form-error" role="alert">{error}</div>}

      <section className="panel settings-card">
        <div className="settings-heading"><div><strong>运行参数</strong><span>来自进程环境变量；修改后需要重启服务。</span></div></div>
        <dl className="device-meta">
          <div><dt>版本</dt><dd>{runtime?.version ?? '—'}</dd></div>
          <div><dt>公共根域</dt><dd>{runtime?.base_domain || '—'}</dd></div>
          <div><dt>DNS 供应商</dt><dd>{runtime?.dns_provider ?? '—'}</dd></div>
          <div><dt>单文件上限</dt><dd>{runtime ? bytes(runtime.max_upload_bytes) : '—'}</dd></div>
          <div><dt>预置 key 注册</dt><dd>{runtime?.allow_preprovisioned ? '已开启' : '已关闭'}</dd></div>
          <div><dt>邀请注册</dt><dd>{registration === null ? '—' : registration ? '已开启' : '已关闭'}</dd></div>
        </dl>
        <div className="settings-foot"><Server size={15} />设备中心基线 · <Globe2 size={15} />{runtime?.base_domain || '未配置根域'}</div>
      </section>
    </div>
  );
}
